import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useInventory } from '../../hooks/useInventory';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useProducts } from '../../hooks/useProducts';
import { InventoryMovement, InventoryMovementType } from '../../types/warehouse';
import { Product } from '../../types/product';
import ProductView from '../products/ProductView';
import {
  DocumentTextIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  ArrowsRightLeftIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  AdjustmentsHorizontalIcon,
  ClipboardDocumentCheckIcon,
  BanknotesIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../common/LoadingScreen';
import DateTimePicker from '../common/DateTimePicker';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

interface InventoryDocumentsProps {
  productId?: string;
  warehouseId?: string;
}

interface DocumentWithMovement {
  url: string;
  fileName: string;
  cleanFileName: string;
  fileExtension: string;
  movement: InventoryMovement;
}

const InventoryDocuments: React.FC<InventoryDocumentsProps> = ({
  productId,
  warehouseId
}) => {
  const { t } = useLanguage();
  const { movements: inventoryMovements, loading, error, fetchMovements, removeDocumentFromMovement } = useInventory();
  const { warehouses, loading: warehousesLoading } = useWarehouses();
  const { products, loading: productsLoading } = useProducts();
  
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>(warehouseId || '');
  const [selectedMovementType, setSelectedMovementType] = useState<InventoryMovementType | null>(null);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  
  // Document viewing state
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [isDocumentViewOpen, setIsDocumentViewOpen] = useState(false);
  const [currentViewingMovement, setCurrentViewingMovement] = useState<InventoryMovement | null>(null);
  
  // Confirmation modal state
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [documentUrlToDelete, setDocumentUrlToDelete] = useState<string | null>(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);
  
  // ProductView modal state
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isProductViewOpen, setIsProductViewOpen] = useState(false);

  // Készletmozgások betöltése
  useEffect(() => {
    const loadMovements = async () => {
      const result = await fetchMovements({
        productId,
        warehouseId: selectedWarehouse || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        type: selectedMovementType || undefined,
        limit: 1000
      });
      
      if (result.movements) {
        // Csak azokat a mozgásokat szűrjük, amelyekhez vannak dokumentumok
        const movementsWithDocuments = result.movements.filter(
          movement => movement.attachments && movement.attachments.length > 0
        );
        setMovements(movementsWithDocuments);
      }
    };

    loadMovements();
  }, [productId, selectedWarehouse, fromDate, toDate, selectedMovementType, fetchMovements]);

  // Dokumentumok feldolgozása és szűrése
  const documentsWithMovements = useMemo(() => {
    const allDocuments: DocumentWithMovement[] = [];
    
    movements.forEach(movement => {
      if (movement.attachments && movement.attachments.length > 0) {
        movement.attachments.forEach(url => {
          const fileName = url.split('/').pop()?.split('?')[0] || 'document';
          const cleanFileName = fileName.includes('_') ? fileName.split('_').slice(1).join('_') : fileName;
          const fileExtension = cleanFileName.split('.').pop()?.toLowerCase() || '';
          
          allDocuments.push({
            url,
            fileName,
            cleanFileName,
            fileExtension,
            movement
          });
        });
      }
    });
    
    // Szűrés keresési kifejezés alapján
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      return allDocuments.filter(doc => 
        doc.cleanFileName.toLowerCase().includes(lowerSearchTerm) ||
        doc.movement.productName.toLowerCase().includes(lowerSearchTerm) ||
        doc.movement.productCode.toLowerCase().includes(lowerSearchTerm) ||
        doc.movement.documentNumber?.toLowerCase().includes(lowerSearchTerm) ||
        doc.movement.warehouseName.toLowerCase().includes(lowerSearchTerm)
      );
    }
    
    return allDocuments;
  }, [movements, searchTerm]);

  // Termék megtekintése
  const handleProductClick = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setIsProductViewOpen(true);
    }
  };

  // Dokumentum megtekintése
  const handleDocumentView = (document: DocumentWithMovement) => {
    setSelectedDocuments([document.url]);
    setCurrentViewingMovement(document.movement);
    setIsDocumentViewOpen(true);
  };

  // Dokumentum letöltése
  const downloadDocument = (url: string, filename?: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'document';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Dokumentum törlése
  const handleDeleteDocument = (document: DocumentWithMovement) => {
    setCurrentViewingMovement(document.movement);
    setDocumentUrlToDelete(document.url);
    setIsConfirmDeleteOpen(true);
  };

  // Dokumentum törlésének megerősítése
  const confirmDeleteDocument = async () => {
    if (!currentViewingMovement || !documentUrlToDelete) {
      return;
    }

    setIsDeletingDocument(true);
    setIsConfirmDeleteOpen(false);
    
    try {
      await removeDocumentFromMovement(currentViewingMovement.id, documentUrlToDelete);
      
      toast.success('Dokumentum sikeresen törölve!');
      
      // Lista frissítése
      setTimeout(() => {
        const loadMovements = async () => {
          const result = await fetchMovements({
            productId,
            warehouseId: selectedWarehouse || undefined,
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
            type: selectedMovementType || undefined,
            limit: 1000
          });
          
          if (result.movements) {
            const movementsWithDocuments = result.movements.filter(
              movement => movement.attachments && movement.attachments.length > 0
            );
            setMovements(movementsWithDocuments);
          }
        };
        loadMovements();
      }, 500);
      
    } catch (error) {
      console.error('Hiba a dokumentum törlésekor:', error);
      toast.error('Hiba történt a dokumentum törlésekor');
    } finally {
      setIsDeletingDocument(false);
      setDocumentUrlToDelete(null);
    }
  };

  // Megerősítő modal bezárása
  const cancelDeleteDocument = () => {
    setIsConfirmDeleteOpen(false);
    setDocumentUrlToDelete(null);
    setCurrentViewingMovement(null);
  };

  // Mozgástípus ikon renderelése
  const renderMovementTypeIcon = (type: InventoryMovementType) => {
    switch (type) {
      case InventoryMovementType.STOCK_IN:
        return <ArrowUpTrayIcon className="h-4 w-4 text-green-500" />;
      case InventoryMovementType.STOCK_OUT:
        return <ArrowDownTrayIcon className="h-4 w-4 text-red-500" />;
      case InventoryMovementType.STOCK_TRANSFER:
        return <ArrowsRightLeftIcon className="h-4 w-4 text-blue-500" />;
      case InventoryMovementType.STOCK_ADJUSTMENT:
        return <AdjustmentsHorizontalIcon className="h-4 w-4 text-yellow-500" />;
      case InventoryMovementType.STOCK_INVENTORY:
        return <ClipboardDocumentCheckIcon className="h-4 w-4 text-amber-600" />;
      case InventoryMovementType.STOCK_RESERVATION:
      case InventoryMovementType.STOCK_RESERVATION_RELEASE:
        return <BanknotesIcon className="h-4 w-4 text-slate-500" />;
      default:
        return null;
    }
  };

  if (loading && movements.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4 w-full max-w-full flex-grow">
      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Dokumentumok</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Összes feltöltött dokumentum ({documentsWithMovements.length} db)
          </p>
        </div>
        
        {/* Keresés */}
        <div className="relative w-full lg:w-80">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Keresés dokumentumokban..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-300 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400"
          />
        </div>
      </div>

      {/* Szűrők */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Raktár szűrő */}
          {!warehouseId && warehouses.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedWarehouse("")}
                className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedWarehouse === ""
                    ? "bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 border-2 border-primary-300 dark:border-primary-600"
                    : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                }`}
              >
                <BuildingStorefrontIcon className="h-4 w-4 mr-2" />
                Minden raktár
              </button>
              
              {warehouses.map(warehouse => (
                <button
                  key={warehouse.id}
                  onClick={() => setSelectedWarehouse(warehouse.id)}
                  className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedWarehouse === warehouse.id
                      ? "bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 border-2 border-primary-300 dark:border-primary-600"
                      : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                  }`}
                >
                  <BuildingStorefrontIcon className="h-4 w-4 mr-2" />
                  {warehouse.name}
                </button>
              ))}
            </div>
          )}

          {/* Mozgás típus szűrő */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedMovementType(null)}
              className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                selectedMovementType === null
                  ? "bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 border-2 border-primary-300 dark:border-primary-600"
                  : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              }`}
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Minden típus
            </button>
            
            {Object.values(InventoryMovementType).map((type) => (
              <button
                key={type}
                onClick={() => setSelectedMovementType(type)}
                className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedMovementType === type
                    ? "bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 border-2 border-primary-300 dark:border-primary-600"
                    : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                }`}
              >
                {renderMovementTypeIcon(type)}
                <span className="ml-2">{t(`inventory.movementTypes.${type}`)}</span>
              </button>
            ))}
          </div>

          {/* Dátum szűrők */}
          <div className="flex items-center gap-2">
            <DateTimePicker showTime={false} value={fromDate || undefined} maxDate={toDate || undefined} onChange={(d) => setFromDate(d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0) : null)} placeholder="Kezdő dátum" className="w-40" />
            <span className="text-gray-400">–</span>
            <DateTimePicker showTime={false} value={toDate || undefined} minDate={fromDate || undefined} onChange={(d) => setToDate(d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999) : null)} placeholder="Záró dátum" className="w-40" />
          </div>
        </div>
      </div>

      {/* Dokumentumok listája */}
      {error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
          {error}
        </div>
      ) : documentsWithMovements.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 text-yellow-700 dark:text-yellow-400 px-4 py-3 rounded-md text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-yellow-400 dark:text-yellow-500 mb-4" />
          <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200">Nincsenek dokumentumok</h3>
          <p className="text-yellow-600 dark:text-yellow-300">
            {searchTerm 
              ? 'A keresési feltételeknek megfelelő dokumentumok nem találhatók.'
              : 'Még nincsenek feltöltött dokumentumok a kiválasztott szűrési feltételekkel.'
            }
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Dokumentum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Készletmozgás
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Termék
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Dátum
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {documentsWithMovements.map((document, index) => (
                  <tr key={`${document.movement.id}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          {['jpg', 'jpeg', 'png', 'gif'].includes(document.fileExtension) ? (
                            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          ) : document.fileExtension === 'pdf' ? (
                            <div className="w-8 h-8 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs">
                            {document.cleanFileName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {document.fileExtension.toUpperCase()} fájl
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {renderMovementTypeIcon(document.movement.type)}
                        <div className="ml-2">
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {t(`inventory.movementTypes.${document.movement.type}`)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {document.movement.documentNumber || 'Nincs bizonylat szám'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleProductClick(document.movement.productId)}
                        className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline"
                      >
                        {document.movement.productName}
                      </button>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{document.movement.productCode}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {document.movement.date ? 
                        (() => {
                          try {
                            let dateObj;
                            
                            if (document.movement.date instanceof Date) {
                              dateObj = document.movement.date;
                            } else if (typeof document.movement.date === 'string') {
                              dateObj = new Date(document.movement.date);
                            } else if (typeof document.movement.date === 'object' && document.movement.date !== null) {
                              const timestamp = document.movement.date as { seconds?: number };
                              if (timestamp.seconds) {
                                dateObj = new Date(timestamp.seconds * 1000);
                              } else {
                                dateObj = new Date(document.movement.date as any);
                              }
                            } else {
                              dateObj = new Date(document.movement.date as any);
                            }
                            
                            if (isNaN(dateObj.getTime())) {
                              return 'Érvénytelen dátum';
                            }
                            
                            return format(dateObj, 'yyyy-MM-dd HH:mm');
                          } catch (error) {
                            return 'Érvénytelen dátum';
                          }
                        })() : 
                        'Érvénytelen dátum'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleDocumentView(document)}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/20 hover:bg-primary-200 dark:hover:bg-primary-900/40"
                        >
                          <EyeIcon className="h-3 w-3 mr-1" />
                          Megtekintés
                        </button>
                        <button
                          onClick={() => downloadDocument(document.url, document.cleanFileName)}
                          className="inline-flex items-center px-2 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          <ArrowTopRightOnSquareIcon className="h-3 w-3 mr-1" />
                          Letöltés
                        </button>
                        <button
                          onClick={() => handleDeleteDocument(document)}
                          className="inline-flex items-center px-2 py-1 border border-red-300 dark:border-red-600 text-xs font-medium rounded text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40"
                        >
                          <XMarkIcon className="h-3 w-3 mr-1" />
                          Törlés
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Product View Modal */}
      {selectedProduct && (
        <ProductView
          product={selectedProduct}
          isOpen={isProductViewOpen}
          onClose={() => {
            setIsProductViewOpen(false);
            setSelectedProduct(null);
          }}
        />
      )}

      {/* Document Viewer Modal */}
      {isDocumentViewOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-black dark:bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <DocumentTextIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Dokumentum megtekintése
                </h3>
              </div>
              <button
                onClick={() => {
                  setIsDocumentViewOpen(false);
                  setCurrentViewingMovement(null);
                  setSelectedDocuments([]);
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {selectedDocuments.map((url, index) => {
                const fileName = url.split('/').pop()?.split('?')[0] || `document-${index + 1}`;
                const cleanFileName = fileName.includes('_') ? fileName.split('_').slice(1).join('_') : fileName;
                const fileExtension = cleanFileName.split('.').pop()?.toLowerCase();
                
                return (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension || '') ? (
                          <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        ) : fileExtension === 'pdf' ? (
                          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                            <DocumentTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {cleanFileName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {fileExtension?.toUpperCase()} fájl
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => window.open(url, '_blank')}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/20 hover:bg-primary-200 dark:hover:bg-primary-900/40 transition-colors"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        Megnyitás
                      </button>
                      <button
                        onClick={() => downloadDocument(url, cleanFileName)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                        Letöltés
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-600 mt-6">
              <button
                onClick={() => {
                  setIsDocumentViewOpen(false);
                  setCurrentViewingMovement(null);
                  setSelectedDocuments([]);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {isConfirmDeleteOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-black dark:bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-md shadow-lg rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Dokumentum törlése
                </h3>
              </div>
              <button
                onClick={cancelDeleteDocument}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Biztosan törölni szeretnéd ezt a dokumentumot?
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Ez a művelet nem vonható vissza.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDeleteDocument}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Mégse
              </button>
              <button
                onClick={confirmDeleteDocument}
                disabled={isDeletingDocument}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed transition-colors"
              >
                {isDeletingDocument ? 'Törlés...' : 'Törlés'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryDocuments; 